import {
  ChangeDetectionStrategy,
  Component,
  type TemplateRef,
  ViewContainerRef,
  computed,
  inject,
  input,
  signal,
  viewChild,
  Output,
  EventEmitter,
  ViewChild,
  type ElementRef,
  type OnInit,
  type OnDestroy,
  type AfterViewInit,
  type AfterViewChecked,
  HostListener,
  ChangeDetectorRef,
  DestroyRef,
} from '@angular/core';
import {
  BreakpointObserverService,
  CategoryHierarchyService,
  HREF,
  OverlayService,
  NavigationService,
  ProductService,
  APPLE_STORE_PAGES_ENABLED,
} from '@core';

import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { AccessibleClickDirective } from 'libs/shared/src/lib/directives/accessible-click.directive';
import { MobileOverlayComponent } from 'libs/shared/src/lib/mobile-overlay/mobile-overlay.component';
import { ThemeService } from 'libs/shared/src/lib/services/theme.service';
import type { INavigationMenuAction } from '../../interfaces';
import { NavigationMenuHeaderComponent } from '../navigation-menu-header/navigation-menu-header.component';
import { NavigationMenuService } from '../navigation-menu-mobile/navigation-menu.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { findProductIdFromCategory } from '../../../../../../../../apps/merch-store/src/app/pages/category-apple/category-to-product.util';
import { TertiaryAppleNavigationService } from './tertiary-nav.service';
import { catchError, distinctUntilChanged, filter, forkJoin, map, type Observable, of, startWith } from 'rxjs';

// Navigation Interfaces
export interface NavigationItem {
  id: string;
  label: string;
  href?: string;
  isActive?: boolean;
}

// Tertiary Navigation Interfaces
export interface TertiaryNavItem {
  id: string;
  productName: string;
  imageBlk: string;
  imageWht: string;
  isNew?: boolean;
  categoryId?: string;
}

export interface BrandItem {
  id: string;
  name: string;
  logoSrc: string;
  href?: string;
}

export interface AppleNavData {
  showTertiaryNav?: boolean;
  tertiaryItems?: TertiaryNavItem[];
  brandItems?: BrandItem[];
  searchPlaceholder?: string;
}

@Component({
  selector: 'lib-navigation-menu',
  templateUrl: './navigation-menu.component.html',
  styleUrls: ['./navigation-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatIconModule,
    MobileOverlayComponent,
    NavigationMenuHeaderComponent,
    AccessibleClickDirective,
  ],
  providers: [TertiaryAppleNavigationService],
})
export class NavigationMenuComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  mobileOverlayComponent = viewChild<TemplateRef<unknown>>('overlayTemplate');
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private productService = inject(ProductService);
  actions = input.required<INavigationMenuAction[]>();

  // Existing properties
  selectedAction = signal<INavigationMenuAction | null>(null);
  isShowSelectedAction = signal(false);

  // Convert mobile state to signals for OnPush change detection
  isMobileCategoryOpen = signal(false);
  isMobileLayout = signal(false);
  constructor(private tertiaryNavService: TertiaryAppleNavigationService) {}
  vm = computed(() => ({
    selectedAction: this.selectedAction(),
    isShowSelectedAction: this.isShowSelectedAction(),
    actions: this.actions(),
    isMobile: this.isMobile(),
    isTabletOrLower: this.isTabletOrLower(),
    isMobileCategoryOpen: this.isMobileCategoryOpen(),
    isMobileLayout: this.isMobileLayout(),
    //new temp
    brandId: this.brandId(),
    productId: this.productId(),
    secondaryShouldHideMenu: this.secondaryShouldHideMenu(),
    tertiaryShouldHideMenu: this.tertiaryShouldHideMenu(),
    queryParams: this.currentQueryParams(),
    categoryId: this.categoryId(),
    navigationData: this.navigationData(),
  }));

  isTabletOrLower = inject(BreakpointObserverService).isTabletOrLower;
  isMobile = inject(BreakpointObserverService).isMobile;
  overlayService = inject(OverlayService);
  viewContainerRef = inject(ViewContainerRef);
  navigationMenuMobileService = inject(NavigationMenuService);
  private cdr = inject(ChangeDetectorRef);
  private navigationService = inject(NavigationService);
  private hierarchyService = inject(CategoryHierarchyService);
  readonly themeService = inject(ThemeService);

  navigationData = computed(() => this.tertiaryNavService.getNavigationData(this.effectiveCategoryId()));

  @Output() tertiaryItemClick = new EventEmitter<TertiaryNavItem>();
  @Output() brandItemClick = new EventEmitter<BrandItem>();
  @Output() navigationItemClick = new EventEmitter<NavigationItem>();
  @Output() categoryClick = new EventEmitter<void>();
  @Output() brandClick = new EventEmitter<void>();
  @Output() searchClick = new EventEmitter<void>();
  @Output() searchInput = new EventEmitter<string>();

  // ViewChild References for Navigation Overflow
  @ViewChild('navContainer', { static: false }) navContainer!: ElementRef;
  @ViewChild('navList', { static: false }) navList!: ElementRef;
  @ViewChild('leftSection', { static: false }) leftSection!: ElementRef;
  @ViewChild('rightSection', { static: false }) rightSection!: ElementRef;
  @ViewChild('logo', { static: false }) logo!: ElementRef;
  @ViewChild('desktopSearchInput', { static: false }) desktopSearchInput!: ElementRef;
  @ViewChild('mobileSearchInput', { static: false }) mobileSearchInput!: ElementRef;

  // Tertiary Navigation ViewChild References
  @ViewChild('tertiaryContainer', { static: false }) tertiaryContainer!: ElementRef;
  @ViewChild('tertiaryGrid', { static: false }) tertiaryGrid!: ElementRef;

  // Static secondary navigation items
  secondaryNavItems: NavigationItem[] = [
    { id: 'mac', label: 'Mac', href: '/mac' },
    { id: 'ipad', label: 'iPad', href: '/ipad' },
    { id: 'iphone', label: 'iPhone', href: '/iphone' },
    { id: 'watch', label: 'Watch', href: '/watch' },
    { id: 'airpods', label: 'AirPods', href: '/airpods' },
    { id: 'airtag', label: 'AirTag', href: '/airtag' },
    { id: 'tv-home', label: 'TV & Home', href: '/tv-home' },
    { id: 'beats', label: 'Beats', href: '/beats' },
    { id: 'accessories', label: 'Accessories', href: '/accessories' },
  ];

  // Maps a nav item href to the category id it navigates to (see onNavigationItemClick)
  private readonly hrefToCategoryId: Record<string, number> = {
    '/mac': 60002,
    '/ipad': 60003,
    '/iphone': 60004,
    '/watch': 60005,
    '/beats': 60006,
    '/airpods': 60007,
    '/tv-home': 60008,
    '/accessories': 60010,
    '/airtag': 62003,
  };

  appleBrandId: number = 1032;

  // Responsive navigation state
  visibleNavItems: NavigationItem[] = [];
  hiddenNavItems: NavigationItem[] = [];
  showOverflowMenu = false;
  isOverflowMenuOpen = false;

  // Brand dropdown state
  isBrandDropdownOpen = false;

  // Search dropdown state
  isSearchDropdownOpen = false;

  // Tertiary Navigation State
  showTertiaryScrollArrows = false;
  canScrollTertiaryLeft = false;
  canScrollTertiaryRight = false;
  tertiaryScrollPosition = 0;

  // Private properties for resize handling and hover timeouts
  private resizeObserver?: ResizeObserver;
  private resizeTimeout?: number;
  private isViewInitialized = false;
  private brandHoverTimeout?: number;
  private searchHoverTimeout?: number;
  private viewCheckedCount = 0; // Track view checked calls

  // Scroll handling properties
  private lastScrollTop = 0;
  private scrollThreshold = 2; // Minimum scroll distance to trigger mobile menu close
  private scrollTimeout?: number;
  tertiaryNavigationService = new TertiaryAppleNavigationService();
  currentQueryParams = signal<any>({});
  brandId = computed(() => this.currentQueryParams()?.brandId || null);
  productId = computed(() => this.currentQueryParams()?.productId || null);
  categoryId = computed(() => this.currentQueryParams()?.categoryId || null);

  isValidatingParams = signal<boolean>(false);
  tertiaryMenuReady = signal<boolean>(false);
  validAppleCategoryId = signal<boolean>(false);
  validAppleProductId = signal<boolean>(false);
  isAppleBrand = signal<boolean>(false);
  fetchedProductData = signal<any>(null);
  categoryHierarchy = signal<any>(null);

  effectiveCategoryId = computed(() => {
    const prodId = this.productId();
    const catId = this.categoryId();

    // If we have a productId but no categoryId, we need to find the correct parent category
    if (prodId && !catId) {
      const productData = this.fetchedProductData();
      const hierarchy = this.categoryHierarchy();

      if (productData && productData.category && productData.category.id && hierarchy) {
        const parentCategoryId = this.findParentBelowAppleDevices(productData.category.id.toString(), hierarchy);

        if (parentCategoryId) {
          return parentCategoryId;
        }
        // Fallback to direct category id if hierarchy traversal fails
        return productData.category.id.toString();
      }
    }

    // Otherwise return the original categoryId
    return catId;
  });

  // Category id of the product currently being viewed (used to highlight the matching tertiary item)
  activeProductCategoryId = computed(() => this.fetchedProductData()?.category?.id?.toString() ?? null);

  // Id of the single tertiary item that should be highlighted: the one whose category is the
  // active category itself, or — failing that — its closest ancestor among the tertiary items.
  // Guarantees only ONE item is active even when several lie on the same hierarchy branch.
  activeTertiaryItemId = computed<string | null>(() => {
    const activeCategoryId = this.activeProductCategoryId() ?? this.categoryId();
    if (!activeCategoryId) {
      return null;
    }
    const activeId = activeCategoryId.toString();
    const items = this.tertiaryItems;

    // Direct match wins outright (we're exactly on this tertiary item's category)
    const direct = items.find((it) => it.categoryId?.toString() === activeId);
    if (direct) {
      return direct.id;
    }

    // Otherwise pick the closest ancestor: walk from the active category up to the root
    // and return the first tertiary item found on that path.
    const hierarchy = this.categoryHierarchy();
    if (!hierarchy) {
      return null;
    }
    const path = this.getCategoryPath(hierarchy, activeId); // root -> active
    if (!path) {
      return null;
    }
    for (let i = path.length - 1; i >= 0; i--) {
      const match = items.find((it) => it.categoryId?.toString() === path[i]);
      if (match) {
        return match.id;
      }
    }
    return null;
  });

  secondaryShouldHideMenu = computed(() => {
    if (!APPLE_STORE_PAGES_ENABLED) {
      return true;
    }

    // Always show if brandId is 1032 (Apple brand)
    if (this.brandId() === this.appleBrandId.toString() || this.isAppleBrand()) {
      return false;
    }

    // Show if we have valid categoryId or productId
    if (this.validAppleCategoryId() || this.validAppleProductId()) {
      return false;
    }

    // Show if we're currently validating (to prevent flickering during navigation)
    if (this.isValidatingParams()) {
      return false;
    }

    // Hide by default if none of the above conditions are met
    return true;
  });
  tertiaryShouldHideMenu = computed(() => {
    if (!APPLE_STORE_PAGES_ENABLED) {
      return true;
    }

    // Hide tertiary menu if we're on the Apple brand page (brandId but no categoryId/productId)
    if (this.brandId() === this.appleBrandId.toString() && !this.categoryId() && !this.productId()) {
      return true;
    }

    // Show tertiary menu if we have valid categoryId or productId
    return !this.validAppleProductId();
  });

  ngOnInit() {
    // Initial mobile detection - use 768px breakpoint like original
    this.checkMobileLayout();
    // Initialize visible items
    this.visibleNavItems = [...this.secondaryNavItems];
    this.hiddenNavItems = [];

    this.productService.productDataLoaded$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ productId, productDetails }) => {
        const currentProductId = this.productId();
        if (currentProductId === productId) {
          this.fetchedProductData.set(productDetails);
          const isValid = productDetails && productDetails.brandId === this.appleBrandId;
          this.validAppleProductId.set(isValid);

          if (isValid) {
            this.isAppleBrand.set(true);
          }

          this.tertiaryMenuReady.set(true);
          this.isValidatingParams.set(false);
          this.cdr.detectChanges();
        }
      });
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        startWith(null),
        map(() => this.getCurrentParams()),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((params) => {
        this.currentQueryParams.set(params);
        this.validateCurrentParams(params);
      });
  }

  /**
   * Query params of the active route; on a brand page (`marketplace/:id`)
   * `brandId` is taken from the route path.
   */
  private getCurrentParams(): Record<string, string> {
    let route: ActivatedRoute = this.route;
    while (route.firstChild) {
      route = route.firstChild;
    }

    const { snapshot } = route;
    const brandPathId =
      snapshot.routeConfig?.path === HREF.BRAND_PRODUCTS_withParam ? snapshot.paramMap.get('id') : null;

    return { ...snapshot.queryParams, ...(brandPathId ? { brandId: brandPathId } : {}) };
  }

  ngAfterViewInit(): void {
    this.isViewInitialized = true;

    // Use multiple attempts to ensure proper initialization
    this.initializeNavigation();

    // Set up resize observer with debouncing to prevent unnecessary recalculations
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.handleResize();
      });

      // Only observe the main navigation container to prevent unnecessary triggers
      if (this.navContainer?.nativeElement) {
        this.resizeObserver.observe(this.navContainer.nativeElement);
      }
    }
  }

  ngAfterViewChecked(): void {
    this.viewCheckedCount++;

    // Only run for the first few checks to avoid infinite loops
    if (this.viewCheckedCount <= 5 && this.isViewInitialized) {
      // Check if tertiary elements are now available
      if (this.showTertiary && this.tertiaryContainer?.nativeElement && this.tertiaryGrid?.nativeElement) {
        setTimeout(() => {
          this.calculateTertiaryScroll();
        }, 0);
      }
    }
  }

  // Fix 1: Improved initialization with better timing and change detection
  private initializeNavigation(): void {
    // Multiple initialization attempts to handle different loading scenarios
    const attempts = [0, 100, 300, 500, 1000, 1500]; // Added longer delays

    attempts.forEach((delay) => {
      setTimeout(() => {
        if (this.isViewInitialized) {
          this.calculateVisibleItems();
          this.calculateTertiaryScroll();
          this.cdr.detectChanges();
        }
      }, delay);
    });
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    // Clean up hover timeouts
    if (this.brandHoverTimeout) {
      clearTimeout(this.brandHoverTimeout);
    }
    if (this.searchHoverTimeout) {
      clearTimeout(this.searchHoverTimeout);
    }
    // Clean up scroll timeout
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.handleResize();
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    const scrollDifference = Math.abs(currentScrollTop - this.lastScrollTop);

    // Clear any existing timeout
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    // Only close mobile menu if there's significant scroll movement
    // This prevents micro-scrolls from radio button interactions from closing the menu
    if (this.isMobileCategoryOpen() && scrollDifference > this.scrollThreshold) {
      // Add a small debounce to prevent rapid firing
      this.scrollTimeout = window.setTimeout(() => {
        this.isMobileCategoryOpen.set(false);
      }, 50);
    }

    this.lastScrollTop = currentScrollTop;
  }

  // Existing methods
  onSelectAction(action: INavigationMenuAction): void {
    this.isShowSelectedAction.set(true);
    this.selectedAction.set(action);
    action.onSelect?.();

    if (this.isTabletOrLower()) {
      this.overlayService.open(this.mobileOverlayComponent()!, this.viewContainerRef);
    }
  }

  closeMenu(): void {
    this.isShowSelectedAction.set(false);
    this.navigationMenuMobileService.dropActiveItem();

    if (this.isTabletOrLower()) {
      this.overlayService.close();
    }
  }

  // Navigation Overflow Methods
  private handleResize(): void {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.resizeTimeout = window.setTimeout(() => {
      // Only recalculate if we're not in the middle of a hover interaction
      if (!this.isBrandDropdownOpen && !this.isSearchDropdownOpen && !this.isOverflowMenuOpen) {
        this.checkMobileLayout();
        this.calculateVisibleItems();
        this.calculateTertiaryScroll();
        this.cdr.detectChanges(); // Add change detection
      }
    }, 150); // Increased debounce time to prevent rapid recalculations
  }

  private checkMobileLayout(): void {
    // Use 767px breakpoint like the original component
    const isMobile = window.innerWidth <= 767;
    if (isMobile !== this.isMobileLayout()) {
      this.isMobileLayout.set(isMobile);
    }
  }

  calculateVisibleItems(): void {
    this.isOverflowMenuOpen = false;

    if (this.isMobileLayout()) {
      this.visibleNavItems = [];
      this.hiddenNavItems = [...this.secondaryNavItems];
      this.showOverflowMenu = false;
      return;
    }

    // If elements aren't ready yet, show all items
    if (!this.navContainer?.nativeElement) {
      this.visibleNavItems = [...this.secondaryNavItems];
      this.hiddenNavItems = [];
      this.showOverflowMenu = false;
      return;
    }

    const containerWidth = this.navContainer.nativeElement.offsetWidth;

    // Updated space calculations - increased right space by 50px for search
    const logoWidth = 170; // Apple logo width
    const containerPadding = 80; // Total horizontal padding
    const overflowButtonWidth = 40;
    const minSpacing = 280; // Increased from 200 to 280 (added 80px for search)

    const availableNavWidth = containerWidth - logoWidth - containerPadding - minSpacing;

    this.visibleNavItems = [];
    this.hiddenNavItems = [];
    this.showOverflowMenu = false;

    let currentWidth = 0;
    const availableWidthWithOverflow = availableNavWidth - overflowButtonWidth;

    for (let i = 0; i < this.secondaryNavItems.length; i++) {
      const item = this.secondaryNavItems[i];
      const itemWidth = this.estimateItemWidth(item.label);
      const gapWidth = i > 0 ? 24 : 0;
      const totalItemWidth = itemWidth + gapWidth;

      if (currentWidth + totalItemWidth <= availableWidthWithOverflow) {
        this.visibleNavItems.push(item);
        currentWidth += totalItemWidth;
      } else {
        this.hiddenNavItems = this.secondaryNavItems.slice(i);
        this.showOverflowMenu = true;
        break;
      }
    }

    // Ensure at least one item is visible if we have items
    if (this.visibleNavItems.length === 0 && this.secondaryNavItems.length > 0) {
      this.visibleNavItems = [this.secondaryNavItems[0]];
      this.hiddenNavItems = this.secondaryNavItems.slice(1);
      this.showOverflowMenu = this.hiddenNavItems.length > 0;
    }
  }

  private estimateItemWidth(label: string): number {
    const avgCharWidth = 8;
    const basePadding = 16;
    return Math.ceil(label.length * avgCharWidth + basePadding);
  }

  onOverflowClick(): void {
    this.isOverflowMenuOpen = !this.isOverflowMenuOpen;
  }

  onOverflowMouseLeave(): void {
    setTimeout(() => {
      this.isOverflowMenuOpen = false;
    }, 150);
  }
  onResellerClick(): void {
    this.navigationService.goToBrandStore(this.appleBrandId);
  }

  onNavigationItemClick(item: NavigationItem): void {
    this.navigationItemClick.emit(item);
    let categoryId: number;

    switch (item.href) {
      case '/mac':
        categoryId = 60002;
        this.navigationService.goToAppleCategoryPage(categoryId);
        break;
      case '/ipad':
        categoryId = 60003;
        this.navigationService.goToAppleCategoryPage(categoryId);
        break;
      case '/iphone':
        categoryId = 60004;
        this.navigationService.goToAppleCategoryPage(categoryId);
        break;
      case '/watch':
        categoryId = 60005;
        this.navigationService.goToAppleCategoryPage(categoryId);
        break;
      case '/beats':
        categoryId = 60006;
        this.navigationService.goToCategoryPage(categoryId);
        break;
      case '/airpods':
        categoryId = 60007;
        this.navigationService.goToAppleCategoryPage(categoryId);
        break;
      case '/tv-home':
        categoryId = 60008;
        this.navigationService.goToAppleCategoryPage(categoryId);
        break;
      case '/accessories':
        categoryId = 60010;
        this.navigationService.goToCategoryPage(categoryId);
        break;
      case '/airtag':
        categoryId = 62003;
        this.navigationService.goToCategoryPage(categoryId);
        break;
      default:
        categoryId = 60001;
        this.navigationService.goToAppleCategoryPage(categoryId);
        break;
    }
    this.isOverflowMenuOpen = false;
    this.isMobileCategoryOpen.set(false);
  }

  // Whether the nav item matches the currently active category (reactive via signals)
  isNavItemActive(item: NavigationItem): boolean {
    const currentCategoryId = this.effectiveCategoryId();
    if (!currentCategoryId || !item.href) {
      return false;
    }
    const itemCategoryId = this.hrefToCategoryId[item.href]?.toString();
    if (!itemCategoryId) {
      return false;
    }

    const current = currentCategoryId.toString();

    // On the top-level category page itself
    if (itemCategoryId === current) {
      return true;
    }

    // If the current category is itself a secondary nav category (e.g. AirTag, which
    // lives under iPhone in the hierarchy), only its exact item should be active — do
    // not fall back to highlighting an ancestor item too.
    if (this.isSecondaryNavCategory(current)) {
      return false;
    }

    // Otherwise highlight the item whose category is an ancestor of the current
    // (sub)category, e.g. Mac Accessories -> Mac
    const hierarchy = this.categoryHierarchy();
    if (!hierarchy) {
      return false;
    }
    return this.findParentBelowAppleDevices(current, hierarchy) === itemCategoryId;
  }

  // True when the given category id is the destination of one of the secondary nav items.
  private isSecondaryNavCategory(categoryId: string): boolean {
    return Object.values(this.hrefToCategoryId).some((id) => id.toString() === categoryId);
  }

  // Whether the tertiary item matches the product or category currently being viewed.
  // Only the single closest item is highlighted (see activeTertiaryItemId).
  isTertiaryItemActive(item: TertiaryNavItem): boolean {
    return !!item.categoryId && this.activeTertiaryItemId() === item.id;
  }

  onMobileCategoryClick(): void {
    this.isMobileCategoryOpen.set(!this.isMobileCategoryOpen());
  }

  calculateTertiaryScroll(): void {
    if (!this.showTertiary || this.tertiaryItems.length === 0) {
      this.showTertiaryScrollArrows = false;
      this.canScrollTertiaryLeft = false;
      this.canScrollTertiaryRight = false;
      return;
    }

    if (!this.tertiaryContainer?.nativeElement || !this.tertiaryGrid?.nativeElement) {
      this.showTertiaryScrollArrows = false;
      this.canScrollTertiaryLeft = false;
      this.canScrollTertiaryRight = false;
      return;
    }

    const container = this.tertiaryContainer.nativeElement;
    const grid = this.tertiaryGrid.nativeElement;

    const containerStyle = window.getComputedStyle(container);
    const containerPaddingLeft = Number.parseFloat(containerStyle.paddingLeft) || 0;
    const containerPaddingRight = Number.parseFloat(containerStyle.paddingRight) || 0;
    const totalContainerPadding = containerPaddingLeft + containerPaddingRight;

    const containerWidth = container.offsetWidth - totalContainerPadding;
    const gridWidth = grid.scrollWidth;
    this.showTertiaryScrollArrows = gridWidth > containerWidth;

    if (this.showTertiaryScrollArrows) {
      this.canScrollTertiaryLeft = true;
      this.canScrollTertiaryRight = true;
    } else {
      this.canScrollTertiaryLeft = false;
      this.canScrollTertiaryRight = false;
    }

    // Trigger change detection to update the UI
    this.cdr.detectChanges();
  }

  onTertiaryItemClick(item: TertiaryNavItem): void {
    this.tertiaryItemClick.emit(item);
    let productId;
    if (item.productName.toLocaleLowerCase()?.includes('accessories') && !!item.categoryId) {
      this.navigationService.goToCategoryPage(item.categoryId);
    } else if (item.categoryId) {
      findProductIdFromCategory(item.categoryId, this.productService).subscribe((newProductId) => {
        productId = +newProductId;
        this.navigationService.goToProductPage(productId, this.appleBrandId);
      });
    }
  }

  onTertiaryScrollLeft(): void {
    if (!this.tertiaryGrid?.nativeElement) {
      return;
    }

    const grid = this.tertiaryGrid.nativeElement;
    const scrollAmount = 240;
    const currentScroll = grid.scrollLeft;

    if (currentScroll <= 10) {
      const containerWidth = this.tertiaryContainer?.nativeElement?.offsetWidth || 0;
      const gridWidth = grid.scrollWidth;
      const maxScroll = gridWidth - containerWidth;

      grid.scrollTo({
        left: maxScroll,
        behavior: 'smooth',
      });
    } else {
      // Normal scroll behavior
      grid.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth',
      });
    }

    setTimeout(() => {
      this.calculateTertiaryScroll();
    }, 300);
  }

  onTertiaryScrollRight(): void {
    if (!this.tertiaryGrid?.nativeElement) {
      return;
    }

    const grid = this.tertiaryGrid.nativeElement;
    const scrollAmount = 240;

    const containerWidth = this.tertiaryContainer?.nativeElement?.offsetWidth || 0;
    const gridWidth = grid.scrollWidth;
    const currentScroll = grid.scrollLeft;
    const maxScroll = gridWidth - containerWidth;

    // If we're at the end (within 10px tolerance), scroll back to the beginning
    if (currentScroll >= maxScroll - 10) {
      grid.scrollTo({
        left: 0,
        behavior: 'smooth',
      });
    } else {
      // Normal scroll behavior
      grid.scrollBy({
        left: scrollAmount,
        behavior: 'smooth',
      });
    }

    setTimeout(() => {
      this.calculateTertiaryScroll();
    }, 300);
  }

  onTertiaryScroll(): void {
    this.calculateTertiaryScroll();
  }

  // Getters
  get showTertiary(): boolean {
    return this.navigationData().showTertiaryNav || false;
  }

  get tertiaryItems(): TertiaryNavItem[] {
    return this.navigationData().tertiaryItems || [];
  }

  get mobileNavItems(): NavigationItem[] {
    return this.secondaryNavItems;
  }

  private validateCurrentParams(queryParams: any): void {
    const categoryId = queryParams?.categoryId;
    const productId = queryParams?.productId;
    const brandId = queryParams?.brandId;

    this.isValidatingParams.set(true);
    this.tertiaryMenuReady.set(false);

    // Early exit if brandId is explicitly set and it's not Apple
    if (brandId && brandId !== this.appleBrandId.toString()) {
      this.validAppleCategoryId.set(false);
      this.validAppleProductId.set(false);
      this.isAppleBrand.set(false);
      this.fetchedProductData.set(null);
      this.categoryHierarchy.set(null);
      this.isValidatingParams.set(false);
      this.tertiaryMenuReady.set(true);
      return;
    }

    // Early exit if categoryId is present but not in Apple's likely range
    if (categoryId && !this.isLikelyAppleCategory(categoryId)) {
      this.validAppleCategoryId.set(false);
      this.validAppleProductId.set(false);
      this.isAppleBrand.set(false);
      this.fetchedProductData.set(null);
      this.categoryHierarchy.set(null);
      this.isValidatingParams.set(false);
      this.tertiaryMenuReady.set(true);
      return;
    }

    // Only reset validation states if we're not on an Apple brand page
    if (brandId !== this.appleBrandId.toString()) {
      this.validAppleCategoryId.set(false);
      this.validAppleProductId.set(false);
      this.isAppleBrand.set(false);
      this.fetchedProductData.set(null);
      this.categoryHierarchy.set(null);
    }

    const validationObservables: Observable<boolean>[] = [];

    // Use hierarchy service instead of direct ProductService calls
    if (categoryId || productId) {
      const hierarchyObservable = this.hierarchyService.getHierarchy().pipe(
        map((hierarchy: any) => {
          this.categoryHierarchy.set(hierarchy);
          return true;
        }),
        catchError((error) => {
          this.categoryHierarchy.set(null);
          return of(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      );

      validationObservables.push(hierarchyObservable);
    }

    // Validate categoryId if present - use cached hierarchy
    if (categoryId) {
      const categoryValidation = this.hierarchyService.getHierarchy().pipe(
        map((hierarchy: any) => {
          const isValid = this.findCategoryInHierarchy(hierarchy, categoryId);
          this.validAppleCategoryId.set(isValid);
          return isValid;
        }),
        catchError((error) => {
          this.validAppleCategoryId.set(false);
          return of(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      );

      validationObservables.push(categoryValidation);
    }

    // Check cache first, only make API call if no cached data exists
    if (productId) {
      const cachedData = this.productService.getCachedProductData(productId);

      if (cachedData) {
        // Use cached data immediately
        this.fetchedProductData.set(cachedData.productDetails);
        const isValid = cachedData.productDetails && cachedData.productDetails.brandId === this.appleBrandId;
        this.validAppleProductId.set(isValid);

        if (isValid) {
          this.isAppleBrand.set(true);
        }
      } else {
        // Set loading state, will be resolved by productDataLoaded$ subscription
        this.validAppleProductId.set(false);
        this.fetchedProductData.set(null);
      }
    }

    // Complete validation
    if (validationObservables.length > 0) {
      forkJoin(validationObservables).subscribe(() => {
        // Only set ready state if we're not waiting for product data
        if (!productId || this.fetchedProductData()) {
          this.isValidatingParams.set(false);
          this.tertiaryMenuReady.set(true);
          this.cdr.detectChanges();
        }
        // If we are waiting for product data, the productDataLoaded$ subscription will handle completion
      });
    } else {
      // No async validation needed
      if (!productId || this.fetchedProductData()) {
        this.isValidatingParams.set(false);
        this.tertiaryMenuReady.set(true);
      }
    }
  }
  private findCategoryInHierarchy(category: any, targetCategoryId: string): boolean {
    if (!category) return false;

    // Check if current category matches
    if (category.id && category.id.toString() === targetCategoryId) {
      return true;
    }

    // Recursively check subcategories
    if (category.subCategories && Array.isArray(category.subCategories)) {
      for (const subCategory of category.subCategories) {
        if (this.findCategoryInHierarchy(subCategory, targetCategoryId)) {
          return true;
        }
      }
    }

    return false;
  }

  // Returns the path of category ids from the hierarchy root down to the target
  // category (inclusive), or null if the target is not present.
  private getCategoryPath(category: any, targetCategoryId: string): string[] | null {
    if (!category) {
      return null;
    }

    const id = category.id != null ? category.id.toString() : null;
    if (id === targetCategoryId) {
      return id ? [id] : null;
    }

    if (Array.isArray(category.subCategories)) {
      for (const subCategory of category.subCategories) {
        const childPath = this.getCategoryPath(subCategory, targetCategoryId);
        if (childPath) {
          return id ? [id, ...childPath] : childPath;
        }
      }
    }

    return null;
  }

  private findParentBelowAppleDevices(targetCategoryId: string, hierarchy: any): string | null {
    const appleDevicesCategory = this.findAppleDevicesCategory(hierarchy);

    if (!appleDevicesCategory || !appleDevicesCategory.subCategories) {
      return null;
    }

    // Look through the direct children of Apple Devices (one layer below)
    for (const directChild of appleDevicesCategory.subCategories) {
      // Check if the target category is this direct child or a descendant of it
      if (this.findCategoryInHierarchy(directChild, targetCategoryId)) {
        return directChild.id?.toString() || null;
      }
    }

    return null;
  }

  private findAppleDevicesCategory(category: any): any | null {
    if (!category) {
      return null;
    }

    // Check if this is the Apple Devices category (id: 60001)
    if (category.id === 60001 || category.name === 'Apple') {
      return category;
    }

    // Recursively search in subcategories
    if (category.subCategories && Array.isArray(category.subCategories)) {
      for (const subCategory of category.subCategories) {
        const found = this.findAppleDevicesCategory(subCategory);
        if (found) {
          return found;
        }
      }
    }

    return null;
  }

  private isLikelyAppleCategory(categoryId: string): boolean {
    const numericId = parseInt(categoryId, 10);

    return numericId >= 60000 && numericId <= 69999;
  }
}
